import React, { useState, useEffect } from "react";
import * as FieldTypes from "../Shared/FieldTypes/FieldTypes.js";
import Error from "../Shared/Error.js";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import TaskManagerShell from "./TaskManagerShell.js";
import ItemDetailsPane from "./ItemDetailsPane.js";
import AddItemPopup from "./AddItemPopup.js";
import "../TaskManagerApp/App.css";

const TaskManager = ({
    emitter,
    paramEntityType = null,
    paramEntityId = null,
    paramIsSlimVersion = false,
    paramExternalLinkedObjects = null,
    paramCurrentCompanyId = null,
    paramCurrentUserId = null,
    paramCompanyId = null,
    paramUserId = null,
    paramHasLimitedWidth = false,
    paramShowAllTasks = false,
    paramIsReviewApproved = false,
    paramShowHeaderlessCountSummaries = false,
}) => {
    const [isLoadingStructure, setIsLoadingStructure] = useState(true);
    const [errorLoadingStructure, setErrorLoadingStructure] = useState(false);
    const [dataTableStructure, setDataTableStructure] = useState(null);

    const loadStructure = () => {
        setIsLoadingStructure(true);
        setErrorLoadingStructure(false);

        const config = {};
        config.dataUrl = "/umbraco/api/TaskManagerApi/TaskList";
        config.getSingleItemUrl = "/umbraco/api/TaskManagerApi/TaskItem";
        config.pageTitle = "Task Manager";
        config.singleItemTitle = "Task";
        config.entityType = "tasks";
        config.entityTypeId = 19;
        config.pageIcon = "fas fa-tasks";
        config.breadcrumbs = [{ Name: "Task Manager", Link: "/your-medboard/task-manager/" }];
        config.dataStructure = [];
        config.enableCustomFields = true;
        config.exportUrl = "/umbraco/api/TaskManagerApi/Export/?taskIds=";
        config.excelExportFileName = "MedBoardTasksExport";
        config.hideTitle = true;
        config.hideBreadcrumbs = true;
        config.hideAddButton = true;
        config.searchPlaceholder = "Search name, description or assignee";
        config.expandable = { countField: "SubtaskCount", parentIdField: "ParentTaskId", expandColumnField: "Name" };
        config.preLoadItemUrlParamName = "loadTaskByDefault";
        config.preLoadItemId = window.loadTaskByDefault || null;

        fetch("/umbraco/api/CustomFields/Headers/?entityType=" + config.entityTypeId)
            .then((response) => {
                if (!response.ok)
                    throw new Error("Network response was not ok");
                return response.json();
            })
            .then((result) => {
                result.forEach((column) => {
                    config.dataStructure.push({
                        customFieldId: column.CustomFieldId,
                        customFieldType: column.CustomFieldType,
                        name: column.Name,
                        field: column.Field,
                        width: column.Width,
                        sortable: column.Sortable,
                        isSelectItemCheckbox: column.IsSelectItemCheckbox,
                        renderWith: FieldTypes[column.RenderWith] ?? FieldTypes.FieldType_Text,
                        isMultiField: column.IsMultiField,
                        disableRowClick: column.DisableRowClick,
                        hasFilter: column.HasFilter,
                    });
                });

                const checkboxIndex = config.dataStructure.findIndex((column) => column.isSelectItemCheckbox);
                if (checkboxIndex > 0) {
                    const [checkboxColumn] = config.dataStructure.splice(checkboxIndex, 1);
                    config.dataStructure.unshift(checkboxColumn);
                } else if (checkboxIndex < 0) {
                    config.dataStructure.unshift({
                        customFieldId: null,
                        customFieldType: null,
                        name: "",
                        field: "Id",
                        width: "25px",
                        sortable: false,
                        isSelectItemCheckbox: true,
                        renderWith: null,
                        isMultiField: null,
                        disableRowClick: false,
                        hasFilter: false,
                    });
                }

                setDataTableStructure(config);
                setIsLoadingStructure(false);
            })
            .catch(() => {
                setErrorLoadingStructure(true);
                setIsLoadingStructure(false);
            });
    };

    useEffect(() => {
        loadStructure();
    }, []);

    if (errorLoadingStructure)
        return <Error message={"Failed to load table configuration"} />;
    if (isLoadingStructure || !dataTableStructure)
        return <LoadingSpinnerImage />;

    return (
        <TaskManagerShell
            baseStructure={dataTableStructure}
            ItemDetailsPane={ItemDetailsPane}
            AddItemPopup={AddItemPopup}
            ReloadStructureFunction={loadStructure}
            emitter={emitter}
            paramEntityType={paramEntityType}
            paramEntityId={paramEntityId}
            paramIsSlimVersion={paramIsSlimVersion}
            paramExternalLinkedObjects={paramExternalLinkedObjects}
            paramHasLimitedWidth={paramHasLimitedWidth}
            paramShowAllTasks={paramShowAllTasks}
            paramIsReviewApproved={paramIsReviewApproved}
            paramShowHeaderlessCountSummaries={paramShowHeaderlessCountSummaries}
            paramCurrentCompanyId={paramCurrentCompanyId || paramCompanyId}
            paramCurrentUserId={paramCurrentUserId || paramUserId}
        />
    );
};

export default TaskManager;
